import api from "@/plugins/api";
import { logger } from "./LoggerService";
const TOUCAN_BASE_URL = import.meta.env.VITE_TOUCAN_BASE_URL || "https://toucan-front.cloud.dh.eudonet.com";
/**
 * API implementation of chart service
 * Fetches charts from the backend API with Keycloak authentication
 */
export class ApiChartService {
    constructor() {
        Object.defineProperty(this, "loadedScripts", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Set()
        });
    }
    /**
     * Fetch all charts from the API
     * Endpoint: GET /api/v1/charts
     * @returns ChartsResult containing charts array and hasData flag
     */
    async getCharts() {
        try {
            logger.debug("Fetching charts from API...");
            const response = await api.get("/charts");
            if (!response.data.success) {
                logger.debug("API returned error:", response.data.errorCode, response.data.message);
                throw new Error(response.data.message || "Failed to fetch charts");
            }
            const apiResult = response.data.result;
            const chartDtos = apiResult?.charts || [];
            const hasData = apiResult?.hasData ?? true; // Default to true for backward compatibility
            logger.debug(`API returned ${chartDtos.length} charts, hasData: ${hasData}`);
            // Map API response to Chart entities
            const charts = chartDtos.map((chartDto, index) => this.mapChartDtoToChart(chartDto, index));
            return { charts, hasData };
        }
        catch (error) {
            logger.debug("Error fetching charts from API:", error);
            throw error;
        }
    }
    /**
     * Map ChartDTO from API to Chart entity
     */
    mapChartDtoToChart(chartDto, index) {
        return {
            id: `chart-${chartDto.embedId}-${index}`,
            title: chartDto.name,
            embedId: chartDto.embedId,
            description: chartDto.description || undefined,
            order: index + 1,
            smallAppId: chartDto.smallAppId || undefined,
            isPublic: chartDto.isPublic,
            expiresIn: chartDto.expiresIn,
            token: chartDto.token || undefined,
            cols: chartDto.cols || 1,
        };
    }
    /**
     * WecComponent Mode
     * Load Toucan script dynamically
     */
    async loadToucanScript(containerId, embedId, token, lang, forceReload = false) {
        const scriptId = `toucan-script-${containerId}`;
        if (forceReload) {
            this.loadedScripts.delete(scriptId);
            const existingScript = document.getElementById(scriptId);
            if (existingScript) {
                existingScript.remove();
            }
        }
        // Check if script is already in the DOM and loaded
        if (this.loadedScripts.has(scriptId) && document.getElementById(scriptId)) {
            return;
        }
        return new Promise((resolve, reject) => {
            const widgetContainer = document.getElementById(containerId);
            if (!widgetContainer) {
                reject(new Error(`Container ${containerId} not found`));
                return;
            }
            // Double check if script exists to avoid race condition where it was added while waiting
            if (document.getElementById(scriptId)) {
                this.loadedScripts.add(scriptId);
                resolve();
                return;
            }
            const script = document.createElement("script");
            script.id = scriptId;
            script.src = this.buildEmbedComponentUrl(embedId, token, lang);
            script.async = true;
            script.type = "text/javascript";
            script.onload = () => {
                // Mark as loaded
                if (document.getElementById(scriptId)) {
                    this.loadedScripts.add(scriptId);
                }
                resolve();
            };
            script.onerror = () => {
                reject(new Error(`Failed to load Toucan script for embed ${embedId}`));
            };
            widgetContainer.appendChild(script);
        });
    }
    /**
     * Cleanup loaded scripts
     */
    cleanup() {
        this.loadedScripts.forEach((scriptId) => {
            const script = document.getElementById(scriptId);
            if (script) {
                script.remove();
            }
        });
        this.loadedScripts.clear();
    }
    /**
     * Build the complete embed URL with authentication and language parameters
     * @param embedId - The Toucan embed ID
     * @param token - JWT token for authentication (optional)
     * @param lang - Language code (optional)
     */
    buildEmbedComponentUrl(embedId, token, lang) {
        const url = new URL(`${TOUCAN_BASE_URL}/scripts/embedLauncher.js`);
        url.searchParams.set("id", embedId);
        if (lang) {
            url.searchParams.set("lang", lang);
        }
        if (token) {
            url.searchParams.set("token", token);
        }
        return url.toString();
    }
}
